import { makeAutoObservable } from 'mobx';
import {
  automationReport,
  createActions, createActionsPush,
  createFilters,
  createSchedule, getAutomationReport,
  getAutomationUsers
} from '../services/automation';
import { reportUsers } from '../services/api';
import fileDownload from 'js-file-download';

class AutomationStore {
  automationUserList = [];
  userStore = null;
  isLoading = false;
  completedSteps = [];
  currentStep = 'filters';
  oneField = ['greatherThan', 'greatherThanOrEqual', 'equal', 'lessThan', 'lessThanOrEqual'];
  twoFields = ['between'];
  automation = null;
  resultSize = 0;
  filters = {
    account_creation_date: {
      type: 'all',
      from: null,
      to: null
    },
    last_access_date: {
      type: 'all',
      from: null,
      to: null
    },
    last_quote_date: {
      type: 'all',
      from: null,
      to: null
    },
    birth_date: {
      type: 'all',
      from: null,
      to: null
    },
    quote_count: {
      type: 'all',
      from: null,
      to: null
    },
    wishlist_count: {
      type: 'all',
      from: null,
      to: null
    },
    profile_privacy: 'all',
    has_interaction: 'no'
  };
  actions = {
    like_last_quote: false,
    follow: false,
    user_id: '',
    automation_id: null
  };
  pushActions = {
    title: null,
    text: null,
    screen: null
  }
  schedule = {
    interval: 5,
    generate_report: false,
    report_emails: '',
    automation_id: null
  };
  alert = null;

  constructor(userStore) {
    this.userStore = userStore;
    const response = getAutomationUsers(this.userStore).then(response => {
      this.automationUserList = response;
      this.actions.user_id = this.automationUserList[0]?.id;
    });

    makeAutoObservable(this);
  }

  setFilter(id, field, value) {
    if (field === undefined || field === null) {
      this.filters[id] = value;
      return;
    }

    this.filters[id][field] = value;
  }

  getFilter(id, field) {
    return this.filters[id][field];
  }

  validateFilters() {
    const keys = Object.keys(this.filters);

    for (let i = 0; i < keys.length; i++) {
      const fieldName = keys[i];
      const field = this.filters[fieldName];

      if (this.oneField.includes(field.type) && (field.from === '' || !field.from)) {
        this.setAlert('Você precisa preencher todos os campos.');
        return false;
      }

      if (this.twoFields.includes(field.type) && (field.from === '' || field.to === '' || !field.from || !field.to)) {
        this.setAlert('Você precisa preencher todos os campos.');
        return false;
      }
    }

    return true;
  }

  setAlert(msg) {
    this.alert = msg;
  }

  clearAlert() {
    this.alert = null;
  }

  validateActions() {
    if (this.actions.follow === false && this.actions.like_last_quote === false) {
      this.setAlert('Você precisa selecionar pelo menos uma ação.');
      return false;
    }

    if (this.actions.user_id === '') {
      this.setAlert('Você precisa selecionar um usuário.');
      return false;
    }

    if (this.actions.automation_id === null) {
      this.setAlert('Você precisa salvar os filtros antes de salvar as ações.');
      return false;
    }

    return true;
  }

  validatePushAction() {
    if (this.automation === null) {
      this.setAlert('Você precisa salvar os filtros antes de salvar as ações.');
      return false;
    }

    if (this.pushActions.title === null || this.pushActions.title.length < 3) {
      this.setAlert('Você precisa preencher o título.');
      return false;
    }

    if (this.pushActions.text === null || this.pushActions.text.length < 3) {
      this.setAlert('Você precisa preencher o texto.');
      return false;
    }

    if (this.pushActions.screen === null || this.pushActions.screen.length < 3) {
      this.setAlert('Você precisa selecionar a tela.');
      return false;
    }

    return true;
  }

  validateSchedule() {
    if (this.schedule.interval === '') {
      this.setAlert('Você precisa selecionar um intervalo.');
      return false;
    }

    if (this.schedule.automation_id === null) {
      this.setAlert('Você precisa salvar os filtros antes de salvar o agendamento.');
      return false;
    }

    if (this.schedule.generate_report && this.schedule.report_emails === '') {
      this.setAlert('Você precisa preencher o campo de e-mails.');
      return false;
    }

    if (this.actionsQty === 0) {
      this.setAlert('Você precisa salvar as ações antes de salvar o agendamento.');
      return false;
    }

    return true;
  }

  get filtersQty() {
    let count = 0;

    if (this.filters.account_creation_date.type !== 'all') count++;
    if (this.filters.last_access_date.type !== 'all') count++;
    if (this.filters.last_quote_date.type !== 'all') count++;
    if (this.filters.birth_date.type !== 'all') count++;
    if (this.filters.quote_count.type !== 'all') count++;
    if (this.filters.wishlist_count.type !== 'all') count++;
    if (this.filters.profile_privacy !== 'all') count++;

    return count;
  }

  get actionsQty() {
    let count = 0;

    if (this.actions.follow) count++;
    if (this.actions.like_last_quote) count++;

    return count;
  }

  get loading() {
    return this.isLoading;
  }

  get selectedUsersCount() {
    return this.resultSize;
  }

  get getCurrentStep() {
    return this.currentStep;
  }

  get actionLikeLastQuote() {
    return this.actions.like_last_quote;
  }

  get actionFollow() {
    return this.actions.follow;
  }

  get actionUserId() {
    return this.actions.user_id;
  }

  get getAutomationUserList() {
    return this.automationUserList;
  }

  get scheduleInterval() {
    return this.schedule.interval;
  }

  get scheduleGenerateReport() {
    return this.schedule.generate_report;
  }

  get scheduleReportEmails() {
    return this.schedule.report_emails;
  }

  get alertMessage() {
    return this.alert;
  }

  get pushTitle() {
    return this.pushActions.title ?? '';
  }

  get pushText() {
    return this.pushActions.text ?? '';
  }

  get pushScreen() {
    return this.pushActions.screen ?? '';
  }

  get estimateTimeExecution() {
    const seconds = this.schedule.interval * this.resultSize * this.actionsQty;

    if (seconds < 60) {
      return `${seconds} segundos`;
    }

    if (seconds === 60) {
      return `1 minuto`;
    }

    if (seconds > 60 && seconds < 3600) {
      return `${Math.floor(seconds / 60)} minutos e ${seconds % 60} segundos`;
    }

    if (seconds === 3600) {
      return `1 hora`;
    }

    if (seconds > 3600 && seconds < 86400) {
      return `${Math.floor(seconds / 3600)} horas e ${Math.floor((seconds % 3600) / 60)} minutos e ${seconds % 60} segundos`;
    }
  }

  setScheduleInterval(interval) {
    this.schedule.interval = interval;
  }

  setScheduleGenerateReport(generateReport) {
    this.schedule.generate_report = generateReport;
  }

  setScheduleReportEmails(reportEmails) {
    this.schedule.report_emails = reportEmails;
  }

  setCurrentStep(step) {
    this.currentStep = step;
  }

  async saveFilters() {
    if (this.filtersQty === 0) {
      this.setAlert('Você precisa selecionar pelo menos um filtro.');
      return;
    }

    if (!this.validateFilters()) return;

    this.isLoading = true;

    try {
      const response = await createFilters(this.userStore, this.filters);
      this.isLoading = false;
      this.automation = response.data.automation;
      this.completedSteps.push('filters');
      this.resultSize = response.data.result_size;
      this.actions.automation_id = this.automation.id;
      this.schedule.automation_id = this.automation.id;
      this.filters.automation_id = this.automation.id;
      this.setCurrentStep('actions');
      this.setAlert(`${this.resultSize} usuários foram selecionados.`);
    } catch (e) {
      this.isLoading = false;
      this.setAlert(e.response?.data?.error?.message ?? 'Houve um erro ao salvar os filtros, tente novamente.');
    }
  }

  async saveActions() {
    if (!this.validateActions()) return;

    this.isLoading = true;

    try {
      const response = await createActions(this.userStore, this.actions);
      this.isLoading = false;
      this.completedSteps.push('actions');
      this.setCurrentStep('schedule');
      return response;
    } catch (e) {
      this.isLoading = false;
      this.setAlert(e.response.data.error.message);
    }
  }

  async saveTextAndScreen() {
    if (!this.validatePushAction()) return;

    this.isLoading = true;

    try {
      await createActionsPush(this.userStore, {...this.pushActions, user_id: this.userStore.user.id, automation_id: this.automation.id});
      this.isLoading = false;
      this.completedSteps.push('actions');
      this.setCurrentStep('schedule');
    } catch (e) {
      this.isLoading = false;
      this.setAlert(e.response.data.error.message);
    }
  }

  async sendPushes() {
    this.isLoading = true;

    try {
      await createSchedule(this.userStore, {
        interval: 0,
        generate_report: false,
        report_emails: '',
        automation_id: this.automation.id
      });

      this.isLoading = false;
      this.setCurrentStep('finish');
    } catch (e) {
      this.isLoading = false;
      this.setAlert(e.response.data.error.message);
    }
  }
  async saveSchedule() {
    if (!this.validateSchedule()) return;

    this.isLoading = true;

    try {
      await createSchedule(this.userStore, this.schedule);
      this.isLoading = false;
      this.completedSteps.push('schedule');
      this.setCurrentStep('finish');
    } catch (e) {
      this.isLoading = false;
      this.setAlert(e.response.data.error.message);
    }
  }

  setAction(field, value) {
    this.actions[field] = value;
  }

  setPushTitle(value) {
    if (value.length > 35) {
      return;
    }

    this.pushActions.title = value;
  }

  setPushText(value) {
    if (value.length > 120) {
      return;
    }

    this.pushActions.text = value;
  }

  setPushScreen(value) {
    this.pushActions.screen = value;
  }

  async downloadReport() {
    this.isLoading = true;
    await getAutomationReport(this.userStore, this.automation.id, () => this.isLoading = false, () => {
      this.isLoading = false;
      this.setAlert('Erro ao baixar o arquivo. Tente novamente.');
    });
  }
}

export default AutomationStore;
